import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import "./../styles/Navbar.css";
import "./../styles/NeonButton.css";
import logo from "../assets/logo.png";
import MenuIcon from "./MenuIcon";

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [lastScrollTop, setLastScrollTop] = useState(0);
  const [visible, setVisible] = useState(true); // Controla la visibilidad de la navbar
  const menuRef = useRef(null); // Ref para el menú desplegable
  const toggleRef = useRef(null); // Ref para el botón de toggle
  const location = useLocation();

  // Cerrar el menú cuando la ruta cambia
  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  // Función para alternar entre abrir y cerrar el menú
  const toggleMenu = () => {
    // Si el menú se está abriendo, bloqueamos el scroll del body
    if (!menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      // Si el menú se está cerrando, restauramos el scroll
      document.body.style.overflow = 'auto';
    }
    
    setMenuOpen((prevState) => !prevState);
  };

  // Cierra el menú si se hace clic fuera del menú desplegable o en el botón de toggle
  const handleCloseMenu = (event) => {
    if (
      menuRef.current &&
      !menuRef.current.contains(event.target) &&
      !toggleRef.current.contains(event.target)
    ) {
      setMenuOpen(false);
      document.body.style.overflow = 'auto';
    }
  };

  useEffect(() => {
    // Añadir eventos de clic y scroll para cerrar el menú
    document.addEventListener("mousedown", handleCloseMenu);
    document.addEventListener("touchstart", handleCloseMenu); // Agregar soporte para touch
    
    window.addEventListener("scroll", () => {
      if (menuOpen) {
        setMenuOpen(false);
        document.body.style.overflow = 'auto';
      }
    });

    return () => {
      document.removeEventListener("mousedown", handleCloseMenu);
      document.removeEventListener("touchstart", handleCloseMenu); // Limpieza de listener de touch
      window.removeEventListener("scroll", () => {
        if (menuOpen) {
          setMenuOpen(false);
          document.body.style.overflow = 'auto';
        }
      });
      // Asegurar que el scroll se restaura al desmontar
      document.body.style.overflow = 'auto';
    };
  }, [menuOpen]);

  useEffect(() => {
    const handleScroll = () => {
      const currentScroll =
        window.pageYOffset || document.documentElement.scrollTop;

      if (currentScroll > lastScrollTop) {
        // Scroll hacia abajo - oculta la navbar
        setVisible(false);
      } else {
        // Scroll hacia arriba - muestra la navbar
        setVisible(true);
      }

      setLastScrollTop(currentScroll <= 0 ? 0 : currentScroll); // Evitar valores negativos
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [lastScrollTop]); // Re-ejecutar solo cuando lastScrollTop cambie

  // Función para manejar la navegación
  const handleNavigation = (e, path) => {
    setMenuOpen(false);
    document.body.style.overflow = 'auto';
    
    // Si ya estamos en esa ruta, prevenir la navegación y solo cerrar el menú
    if (location.pathname === path && path !== '/') {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <nav className={`navbar ${visible ? "" : "navbar-hidden"}`}>
      <div className="navbar-container">
        <a
          href="/"
          onClick={(e) => handleNavigation(e, "/")}
          className="navbar-logo"
        >
          <img src={logo} alt="Logo" className="logo-img" />
          <span className="navbar-title">
            <span>Proyecto</span>
            <span>Chicas Rosas</span>
          </span>
        </a>
        <button
          ref={toggleRef}
          className="menu-toggle"
          onClick={toggleMenu}
          aria-label="Toggle menu"
        >
          <MenuIcon isOpen={menuOpen} />
        </button>
        <ul
          ref={menuRef}
          className={`navbar-menu ${menuOpen ? "show-menu" : ""}`}
        >
          <div className="mobile-menu-header">
            <img src={logo} alt="Logo" className="mobile-menu-logo" />
            <div className="mobile-menu-title-wrapper">
              <div className="mobile-menu-title">Proyecto</div>
              <div className="mobile-menu-title">Chicas Rosas</div>
            </div>
          </div>
          <div className="menu-items-container">
            <div className="menu-items-top">
              <li>
                <Link
                  to="/"
                  onClick={(e) => handleNavigation(e, "/")}
                  className={`navbar-link ${location.pathname === '/' ? 'active' : ''}`}
                >
                  Inicio
                </Link>
              </li>
              <li>
                <Link
                  to="/asociacion"
                  onClick={(e) => handleNavigation(e, "/asociacion")}
                  className={`navbar-link ${location.pathname === '/asociacion' ? 'active' : ''}`}
                >
                  La Asociación
                </Link>
              </li>
              <li>
                <Link
                  to="/actividades"
                  onClick={(e) => handleNavigation(e, "/actividades")}
                  className={`navbar-link ${location.pathname === '/actividades' ? 'active' : ''}`}
                >
                  Actividades
                </Link>
              </li>
              <li>
                <Link
                  to="/proxima-edicion-camino"
                  onClick={(e) => handleNavigation(e, "/proxima-edicion-camino")}
                  className={`navbar-link ${location.pathname === '/proxima-edicion-camino' ? 'active' : ''}`}
                >
                  Camino Santiago 2026
                </Link>
              </li>
              <li>
                <Link
                  to="/recomendaciones-autocuidado"
                  onClick={(e) => handleNavigation(e, "/recomendaciones-autocuidado")}
                  className={`navbar-link ${location.pathname === '/recomendaciones-autocuidado' ? 'active' : ''}`}
                >
                  Cómo Cuidarte
                </Link>
              </li>
            </div>
            
            {/* Botón solo para desktop */}
            <li className="desktop-only-button">
              <Link
                to="/contacto"
                onClick={(e) => handleNavigation(e, "/contacto")}
                className="button-link-wrapper"
              >
                <span className="neon-button button-center">
                  Camina con nosotras
                </span>
              </Link>
            </li>
            
            {/* Botón solo para móvil */}
            <div className="menu-items-bottom">
              <Link
                to="/contacto"
                onClick={(e) => handleNavigation(e, "/contacto")}
                className={`mobile-contact-button ${location.pathname === '/contacto' ? 'active' : ''}`}
              >
                Camina con nosotras
              </Link>
            </div>
          </div>
        </ul>
      </div>
    </nav>
  );
};

export default Navbar;
